import React, { useEffect, useRef, useState } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useParams, useNavigate } from "react-router-dom";
import SidebarAdmin from "../Utils/SidebarAdmin";
import apiConfig from "../../config/apiConfig";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDriveViewModel } from "../../viewModels/useDriveViewModel";
import styles from "../Styles/CreateCertificate.module.css";
import LogoutButton from "../LogoutButton";
import { apiFetch } from "../../apis/apiFetch";
import { ArrowLeft } from "lucide-react";

const DriveEdit = () => {
  const { id }: any = useParams();
  const navigate = useNavigate();

  const baseUrl = apiConfig.getResourceUrl("Drive");
  const apiUrl = `${apiConfig.getResourceUrl("Drive")}?`;
  const metadataUrl = `${apiConfig.getResourceMetaDataUrl("Drive")}?`;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editedRecord, setEditedRecord] = useState<any>({});
  const [showToast, setShowToast] = useState(false);

  const regex = /^(g_|archived|extra_data)/;
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const {
    setFields,
    enums,
    setEnums,
  } = useDriveViewModel("admin@rasp.com", appId);

  // ---------- Fetch By ID ----------
  const { data: fetchedDataById, isLoading: loadingEditComp } = useQuery({
    queryKey: ["getById", "Drive", id],
    queryFn: async () => {
      const params = new URLSearchParams({
        args: `id:${id}`,
        queryId: "GET_BY_ID",
      });

      const res = await apiFetch(`${baseUrl}?${params.toString()}`);
      if (!res.ok) throw new Error("Fetch failed");
      return res.json();
    },
    enabled: !!id,
  });

  // ---------- Set Data ----------
  useEffect(() => {
    if (fetchedDataById?.resource?.length > 0) {
      const resourceData = fetchedDataById.resource[0];

      const newData: any = {};

      Object.entries(resourceData).forEach(([key, val]) => {
        if (!regex.test(key)) {
          if (
            (key === "startdate" ||
              key === "enddate" ||
              key === "editstartdate" ||
              key === "editenddate" ||
              key === "alloweditingenddate" ||
              key === "jee_editing_end_date") &&
            val
          ) {
            const d = new Date(val as string);
            if (!isNaN(d.getTime())) {
              // Using local timezone methods to ensure the date doesn't shift
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const dd = String(d.getDate()).padStart(2, "0");
              newData[key] = `${yyyy}-${mm}-${dd}`;
            } else {
              newData[key] = val;
            }
          } else {
            newData[key] = val;
          }
        }
      });

      setEditedRecord(newData);
    }
  }, [fetchedDataById]);

  // ---------- Metadata + ENUM FIX ----------
  useQuery({
    queryKey: ["resMetaData", "driveEdit"],
    queryFn: async () => {
      const res = await apiFetch(metadataUrl);
      const data = await res.json();

      setFields(data[0].fieldValues);

      // ✅ FIX: fetch enum values
      const enumFields = data[0].fieldValues.filter(
        (f: any) => f.isEnum === true
      );

      for (const field of enumFields) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);

          const enumData = await fetchEnum(field.possible_value);
          setEnums((prev: any) => ({
            ...prev,
            [field.possible_value]: enumData,
          }));
        }
      }

      return data;
    },
  });

  const handleEdit = (field: string, value: any) => {
    setEditedRecord((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const base64EncodeFun = (str: string) => {
    return btoa(unescape(encodeURIComponent(str)));
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editedRecord["name"]) return alert("Name is required");
    if (!editedRecord["drivetype"]) return alert("Drive type is required");

    if (!editedRecord["startdate"] || !editedRecord["enddate"])
      return alert("Start and End dates are required");

    const params = new FormData();
    const encoded = base64EncodeFun(JSON.stringify(editedRecord));

    params.append("resource", encoded);
    params.append("action", "MODIFY");

    const res = await apiFetch(apiUrl, {
      method: "POST",
      body: params,
    });

    if (res.ok) {
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        navigate("/createdrive");
      }, 1500);
    }
  };

  return (
    <div className="dash-layout">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p: any) => !p)}
      />

      <div className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""}`}>
        <header className="dash-header">
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-link p-0 me-2 text-white d-flex align-items-center" 
              onClick={() => navigate("/createdrive")}
              title="Back to Create Drives"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="dash-page-title m-0">Edit Drive</h1>
          </div>

          <div className="dash-user-profile">
            <div className="dash-profile-circle" onClick={() => setShowDropdown(p => !p)}>
              <span>A</span>
            </div>

            {showDropdown && (
              <div className="dash-profile-dropdown">
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        <div className="dash-content-body">
          {!loadingEditComp && (
            <div className="container mt-4">
              <form>
                <div className={styles.batchformCard}>
                  <div className={styles.certificateFormWrapper}>
                    <h2 className={styles.sectionTitle}>Edit Drive</h2>

                    <div className={styles.formGrid}>

                      {/* Name */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          <span className={styles.required}>*</span> Name
                        </label>
                        <input
                          type="text"
                          className={styles.formControl}
                          value={editedRecord["name"] || ""}
                          onChange={(e) => handleEdit("name", e.target.value)}
                        />
                      </div>

                      {/* Drive Type */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          <span className={styles.required}>*</span> Drive Type
                        </label>
                        <select
                          className={styles.formControl}
                          value={editedRecord["drivetype"] || ""}
                          onChange={(e) => handleEdit("drivetype", e.target.value)}
                        >
                          <option value="">Select Drive Type</option>
                          {enums["Drivetype"]?.map((val: any, idx: any) => (
                            <option key={idx} value={val}>{val}</option>
                          ))}
                        </select>
                      </div>

                      {/* Start Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          <span className={styles.required}>*</span> Start Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["startdate"] ? dayjs(editedRecord["startdate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("startdate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* End Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          <span className={styles.required}>*</span> End Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["enddate"] ? dayjs(editedRecord["enddate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("enddate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* JEE Advanced Edit Start Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          JEE Advanced Edit Start Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["editstartdate"] ? dayjs(editedRecord["editstartdate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("editstartdate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* JEE Advanced Edit End Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                         JEE Advanced Edit End Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["editenddate"] ? dayjs(editedRecord["editenddate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("editenddate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* Editing End Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Editing End Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["alloweditingenddate"] ? dayjs(editedRecord["alloweditingenddate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("alloweditingenddate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* JEE Editing End Date */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          JEE Verification Editing End Date
                        </label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["jee_editing_end_date"] ? dayjs(editedRecord["jee_editing_end_date"]) : null}
                            onChange={(newValue) => {
                              handleEdit("jee_editing_end_date", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    "& fieldset": {
                                      borderColor: '#d0d0d0',
                                      borderWidth: '1px',
                                      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                    },
                                    "&:hover fieldset": { borderColor: '#d0d0d0' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#2196f3',
                                      boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": {
                                    padding: '10px 0px',
                                    fontSize: '14px',
                                    color: '#333',
                                    fontFamily: 'inherit',
                                    height: 'auto',
                                  }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* Details */}
                      <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label className={styles.formLabel}>Details</label>
                        <textarea
                          className={styles.textareaControl}
                          value={editedRecord["details"] || ""}
                          onChange={(e) => handleEdit("details", e.target.value)}
                          rows={3}
                        />
                      </div>

                      {/* BTech Capacity Limits */}
                      {editedRecord["drivetype"] === "Btech" && (
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                          <h3 className={styles.sectionTitle} style={{ fontSize: "1.1rem", marginTop: "10px" }}>B.Tech Total Institute Capacity</h3>
                          <p style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "15px" }}>
                            Enter the absolute total number of seats available for each programme. This is used to calculate vacancies for Sliding Rounds.
                          </p>
                          <table className="table table-bordered" style={{ backgroundColor: "white" }}>
                            <thead>
                              <tr>
                                <th>Programme</th>
                                <th>Total Seats</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>B.Tech CSE</td>
                                <td>
                                  <input type="number" className="form-control" value={editedRecord["btech_cse_total_seats"] || ""} onChange={(e) => handleEdit("btech_cse_total_seats", parseInt(e.target.value) || 0)} />
                                </td>
                              </tr>
                              <tr>
                                <td>B.Tech ECE</td>
                                <td>
                                  <input type="number" className="form-control" value={editedRecord["btech_ece_total_seats"] || ""} onChange={(e) => handleEdit("btech_ece_total_seats", parseInt(e.target.value) || 0)} />
                                </td>
                              </tr>
                              <tr>
                                <td>B.Tech AI&amp;DS</td>
                                <td>
                                  <input type="number" className="form-control" value={editedRecord["btech_ai_total_seats"] || ""} onChange={(e) => handleEdit("btech_ai_total_seats", parseInt(e.target.value) || 0)} />
                                </td>
                              </tr>
                              <tr>
                                <td>Integrated M.Tech CSE</td>
                                <td>
                                  <input type="number" className="form-control" value={editedRecord["imtech_cse_total_seats"] || ""} onChange={(e) => handleEdit("imtech_cse_total_seats", parseInt(e.target.value) || 0)} />
                                </td>
                              </tr>
                              <tr>
                                <td>Integrated M.Tech ECE</td>
                                <td>
                                  <input type="number" className="form-control" value={editedRecord["imtech_ece_total_seats"] || ""} onChange={(e) => handleEdit("imtech_ece_total_seats", parseInt(e.target.value) || 0)} />
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )}

                    </div>

                    <div className={styles.buttonRow}>
                      <button className={styles.primaryBtn} onClick={handleUpdate}>
                        Submit
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {showToast && (
                <div className="toast-container position-fixed top-20 start-50 translate-middle p-3">
                  <div className="toast show">
                    <div className="toast-header">
                      <strong className="me-auto">Success</strong>
                      <button className="btn-close" onClick={() => setShowToast(false)} />
                    </div>
                    <div className="toast-body text-success text-center">
                      Updated successfully!
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriveEdit;